// UC 34261 — Dispatch Outlet Transfer Orders (Commissary Supervisor)
// UC 34262 — Confirm Transfer Receipt and Log Discrepancies (Outlet Manager)
// Mirrors RawHTMLDesign/transfer-&-delivery-tracker/transfer-dispatch-&-receipt-page.html

import { useEffect, useState, useCallback, useMemo } from 'react';
import api from '../../../services/api.js';
import { useAuth } from '../../../utils/AuthContext.jsx';

const REFRIG_OPTIONS = [
  'Cold chain verified',
  'Slightly elevated - monitored',
  'Needs maintenance hold',
];

const STATUS_OPTIONS = ['ALL', 'Pending', 'Dispatched', 'Received', 'Discrepancy Logged'];

function statusBadge(status) {
  switch (status) {
    case 'Pending':             return { background: '#f59e0b', color: '#fff' };
    case 'Dispatched':          return { background: '#3b82f6', color: '#fff' };
    case 'Received':            return { background: '#22c55e', color: '#fff' };
    case 'Discrepancy Logged':  return { background: '#ef4444', color: '#fff' };
    default:                    return { background: '#6b7280', color: '#fff' };
  }
}

function StatusPill({ status }) {
  return (
    <span style={{ ...statusBadge(status), borderRadius: 4, padding: '2px 10px', fontSize: '0.75rem', fontWeight: 600, display: 'inline-block' }}>
      {status}
    </span>
  );
}

function fmtDt(dt) {
  if (!dt) return '--';
  return new Date(dt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function TransferDispatchAndReceiptPage() {
  const { hasRole } = useAuth();
  const isSupervisor = hasRole('CommissarySupervisor', 'SuperAdmin', 'Admin');

  const [transfers, setTransfers] = useState([]);
  const [items, setItems] = useState([]);

  // Active Transit Ledger filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  // New Dispatch (Pending) modal
  const [newDispatchOpen, setNewDispatchOpen] = useState(false);
  const [newForm, setNewForm] = useState({
    outlet_name: '',
    notes: '',
    lines: [{ item_id: '', item_name: '', dispatched_qty: '', unit: '' }],
  });
  const [newError, setNewError] = useState('');
  const [newSubmitting, setNewSubmitting] = useState(false);

  // Execute Dispatch modal (for a pending transfer)
  const [execDispatchTransfer, setExecDispatchTransfer] = useState(null);
  const [execForm, setExecForm] = useState({
    driver_name: '', vehicle_details: '',
    refrigeration_status: 'Cold chain verified',
    dispatch_notes: '',
    dispatched_at: '',
  });
  const [execError, setExecError] = useState('');
  const [execSubmitting, setExecSubmitting] = useState(false);

  // View modal (from Active Transit Ledger)
  const [viewTransfer, setViewTransfer] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  // Receipt modal
  const [receiptTransfer, setReceiptTransfer] = useState(null);
  const [receivedQties, setReceivedQties] = useState({});
  const [discrepancyNotes, setDiscrepancyNotes] = useState('');
  const [receiptError, setReceiptError] = useState('');
  const [receiptSubmitting, setReceiptSubmitting] = useState(false);

  const [banner, setBanner] = useState({ message: '', kind: '' });

  const refresh = useCallback(async () => {
    const [tRes, iRes] = await Promise.all([
      api.get('/transfers').then((x) => x.data.data || []).catch(() => []),
      api.get('/master-data/items').then((x) => (x.data.data || []).filter((i) => i.is_active !== false)).catch(() => []),
    ]);
    setTransfers(tRes);
    setItems(iRes);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // KPIs
  const pendingCount     = transfers.filter((t) => t.status === 'Pending').length;
  const dispatchedCount  = transfers.filter((t) => t.status === 'Dispatched').length;
  const receivedCount    = transfers.filter((t) => t.status === 'Received').length;
  const discrepancyCount = transfers.filter((t) => t.has_discrepancy).length;

  const pendingTransfers = useMemo(() => transfers.filter((t) => t.status === 'Pending'), [transfers]);

  const filteredLedger = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return transfers.filter((t) => {
      if (statusFilter !== 'ALL' && t.status !== statusFilter) return false;
      if (term && ![ t.outlet_name, t.driver_name, t.vehicle_details, t.transfer_ref ]
        .some((f) => (f || '').toLowerCase().includes(term))) return false;
      return true;
    });
  }, [transfers, searchTerm, statusFilter]);

  const discrepancyTransfers = useMemo(
    () => transfers.filter((t) => t.has_discrepancy),
    [transfers]
  );

  // ── New Dispatch (create pending) ──────────────────────────
  function setNewField(n, v) { setNewForm((f) => ({ ...f, [n]: v })); }
  function setNewLine(idx, n, v) {
    setNewForm((f) => {
      const lines = [...f.lines];
      lines[idx] = { ...lines[idx], [n]: v };
      if (n === 'item_id') {
        const it = items.find((i) => i.id === v);
        if (it) { lines[idx].item_name = it.name; lines[idx].unit = it.uom; }
      }
      return { ...f, lines };
    });
  }
  function addNewLine() { setNewForm((f) => ({ ...f, lines: [...f.lines, { item_id: '', item_name: '', dispatched_qty: '', unit: '' }] })); }
  function removeNewLine(idx) { setNewForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) })); }

  async function submitNewDispatch() {
    setNewError('');
    const validLines = newForm.lines.filter((l) => l.item_id && Number(l.dispatched_qty) > 0);
    if (!validLines.length) { setNewError('At least one item with quantity > 0 is required.'); return; }
    setNewSubmitting(true);
    try {
      const res = await api.post('/transfers/pending', {
        outlet_name: newForm.outlet_name || null,
        notes: newForm.notes || null,
        items: validLines.map((l) => ({ item_id: l.item_id, item_name: l.item_name, dispatched_qty: Number(l.dispatched_qty), unit: l.unit })),
      });
      setBanner({ message: `Transfer ${res.data.data.transfer_ref} created — awaiting dispatch assignment.`, kind: 'success' });
      setNewDispatchOpen(false);
      setNewForm({ outlet_name: '', notes: '', lines: [{ item_id: '', item_name: '', dispatched_qty: '', unit: '' }] });
      await refresh();
    } catch (err) {
      setNewError(err?.response?.data?.error || 'Failed to create transfer.');
    } finally { setNewSubmitting(false); }
  }

  // ── Execute Dispatch ────────────────────────────────────────
  function openExecDispatch(transfer) {
    setExecDispatchTransfer(transfer);
    setExecForm({ driver_name: '', vehicle_details: '', refrigeration_status: 'Cold chain verified', dispatch_notes: '', dispatched_at: '' });
    setExecError('');
  }

  async function submitExecDispatch() {
    setExecError('');
    if (!execForm.driver_name.trim()) { setExecError('Driver name is required.'); return; }
    if (!execForm.vehicle_details.trim()) { setExecError('Vehicle number is required.'); return; }
    setExecSubmitting(true);
    try {
      await api.patch(`/transfers/${execDispatchTransfer.id}/dispatch`, {
        driver_name: execForm.driver_name,
        vehicle_details: execForm.vehicle_details,
        refrigeration_status: execForm.refrigeration_status,
        dispatch_notes: execForm.dispatch_notes || null,
        dispatched_at: execForm.dispatched_at || null,
      });
      setBanner({ message: `Transfer ${execDispatchTransfer.transfer_ref} dispatched successfully.`, kind: 'success' });
      setExecDispatchTransfer(null);
      await refresh();
    } catch (err) {
      setExecError(err?.response?.data?.error || 'Dispatch failed.');
    } finally { setExecSubmitting(false); }
  }

  // ── View Transfer ───────────────────────────────────────────
  async function openView(transferId) {
    setViewLoading(true);
    setViewTransfer(null);
    try {
      const res = await api.get(`/transfers/${transferId}`);
      setViewTransfer(res.data.data);
    } catch {
      setBanner({ message: 'Failed to load transfer details.', kind: 'error' });
    } finally { setViewLoading(false); }
  }

  // ── Receipt ─────────────────────────────────────────────────
  async function openReceiptModal(transferId) {
    setReceiptError(''); setDiscrepancyNotes('');
    try {
      const res = await api.get(`/transfers/${transferId}`);
      const t = res.data.data;
      setReceiptTransfer(t);
      const initQties = {};
      for (const item of t.items) initQties[item.id] = String(Number(item.dispatched_qty));
      setReceivedQties(initQties);
    } catch {
      setBanner({ message: 'Failed to load transfer details.', kind: 'error' });
    }
  }

  const hasDiscrepancy = receiptTransfer
    ? receiptTransfer.items.some((item) => Number(receivedQties[item.id] ?? item.dispatched_qty) !== Number(item.dispatched_qty))
    : false;

  async function submitReceipt() {
    setReceiptError('');
    if (!receiptTransfer) return;
    for (const item of receiptTransfer.items) {
      const received = Number(receivedQties[item.id] ?? item.dispatched_qty);
      if (received > Number(item.dispatched_qty)) { setReceiptError(`Over-receipt not allowed for ${item.item_name || 'item'}.`); return; }
      if (received < 0) { setReceiptError('Received quantity cannot be negative.'); return; }
    }
    if (hasDiscrepancy && !discrepancyNotes.trim()) { setReceiptError('A discrepancy note is required when quantities differ.'); return; }
    setReceiptSubmitting(true);
    try {
      await api.post(`/transfers/${receiptTransfer.id}/confirm`, {
        received_items: receiptTransfer.items.map((item) => ({
          transfer_item_id: item.id,
          received_qty: Number(receivedQties[item.id] ?? item.dispatched_qty),
        })),
        discrepancy_notes: discrepancyNotes || null,
      });
      setBanner({ message: `Transfer ${receiptTransfer.transfer_ref} confirmed${hasDiscrepancy ? ' with discrepancy.' : '.'}`, kind: hasDiscrepancy ? 'warning' : 'success' });
      setReceiptTransfer(null);
      await refresh();
    } catch (err) {
      setReceiptError(err?.response?.data?.error || 'Failed to confirm receipt.');
    } finally { setReceiptSubmitting(false); }
  }

  return (
    <div className="container-fluid px-4 py-4" data-testid="transfer-page">
      {/* ── Header ── */}
      <div className="row mb-4">
        <div className="col-12 d-flex justify-content-between align-items-center flex-wrap">
          <div>
            <h1 className="h3 font-weight-bold text-dark mb-1">
              <i className="fa-solid fa-truck-fast mr-2 text-teal" />Transfer Dispatch &amp; Receipt
            </h1>
            <p className="text-muted mb-0">Coordinate dispatch preparation, track active cold-chain transfers, and confirm outlet receipt with discrepancy logging in one shared logistics workspace.</p>
          </div>
          <div className="mt-2 mt-sm-0 d-flex align-items-center flex-wrap justify-content-end" style={{ gap: 8 }}>
            <span className="badge badge-light border px-3 py-2 text-dark shadow-sm">
              <i className="fa-solid fa-user-tag mr-1 text-teal" />
              {isSupervisor ? 'Commissary Supervisor' : 'Outlet Manager'}
            </span>
            {isSupervisor && (
              <button className="btn btn-sm btn-teal" onClick={() => { setNewError(''); setNewDispatchOpen(true); }} data-testid="btn-new-dispatch-header">
                <i className="fa-solid fa-plus mr-1" />New Dispatch
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Banner ── */}
      {banner.message && (
        <div className={`alert mb-3 ${banner.kind === 'error' ? 'alert-danger' : banner.kind === 'warning' ? 'alert-warning' : 'alert-success'}`} data-testid="transfer-banner">
          {banner.message}
          <button type="button" className="close ml-2" onClick={() => setBanner({ message: '', kind: '' })}><span>&times;</span></button>
        </div>
      )}

      {/* ── KPI Cards ── */}
      <div className="row mb-4">
        {[
          { label: 'PENDING DISPATCHES',  val: pendingCount,     cls: 'text-dark',    sub: 'Awaiting driver assignment', tid: 'kpi-pending' },
          { label: 'ACTIVE TRANSIT RUNS', val: dispatchedCount,  cls: 'text-teal',    sub: 'Currently dispatched',       tid: 'kpi-transit' },
          { label: 'RECEIVED TRANSFERS',  val: receivedCount,    cls: 'text-success', sub: 'Completed with confirmation', tid: 'kpi-received' },
          { label: 'OPEN DISCREPANCIES',  val: discrepancyCount, cls: 'text-danger',  sub: 'Require central review',     tid: 'kpi-discrepancies' },
        ].map(({ label, val, cls, sub, tid }) => (
          <div className="col-xl-3 col-md-6 mb-3" key={tid}>
            <div className="card shadow-sm border-0 h-100">
              <div className="card-body py-3">
                <small className="text-uppercase text-muted font-weight-600 d-block mb-2" style={{ fontSize: '0.7rem', letterSpacing: '0.05em' }}>{label}</small>
                <div className={`font-weight-bold mb-1 ${cls}`} style={{ fontSize: '2rem', lineHeight: 1 }} data-testid={tid}>{val}</div>
                <small className="text-muted">{sub}</small>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Two-Column Layout ── */}
      <div className="row">
        {/* Left — Pending Dispatches Grid (Supervisor only) */}
        {isSupervisor && <div className="col-xl-5 col-lg-6 mb-4">
          <div className="card shadow-sm h-100">
            <div className="card-header bg-white py-3 border-bottom d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 8 }}>
              <h5 className="card-title mb-0 font-weight-bold text-teal">
                <i className="fa-solid fa-dolly mr-2" />Pending Dispatches Grid
              </h5>
              <span className="badge badge-light border">Pending — awaiting driver assignment</span>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0 text-center" data-testid="dispatch-table">
                  <thead className="thead-light">
                    <tr>
                      <th className="text-left" style={{ paddingLeft: 16 }}>Transfer ID</th>
                      <th className="text-left">Route</th>
                      <th>Items</th>
                      <th>Target Time</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingTransfers.length === 0 && (
                      <tr><td colSpan={6} className="text-muted py-5 text-center" data-testid="pending-empty">
                        <i className="fa-solid fa-truck-ramp-box mb-2 d-block" style={{ fontSize: '2rem', opacity: 0.2 }} />
                        No pending dispatches. Create a new dispatch to begin.
                      </td></tr>
                    )}
                    {pendingTransfers.map((t) => (
                      <tr key={t.id} data-testid={`pending-row-${t.id}`}>
                        <td className="text-left font-weight-bold" style={{ paddingLeft: 16, color: '#008080' }}>{t.transfer_ref}</td>
                        <td className="text-left">
                          <div className="small font-weight-600">Central Kitchen →</div>
                          <div className="small text-muted">{t.outlet_name || '—'}</div>
                        </td>
                        <td>{t.item_count || '—'}</td>
                        <td className="small text-muted">{fmtDt(t.created_at)}</td>
                        <td><StatusPill status={t.status} /></td>
                        <td>
                          <button className="btn btn-sm btn-teal" onClick={() => openExecDispatch(t)} data-testid={`btn-dispatch-${t.id}`}>
                            <i className="fa-solid fa-paper-plane mr-1" />Dispatch
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>}

        {/* Right — Active Transit Ledger + Discrepancy Board */}
        <div className={`${isSupervisor ? 'col-xl-7 col-lg-6' : 'col-12'} mb-4`}>
          {/* Active Transit Ledger */}
          <div className="card shadow-sm mb-4">
            <div className="card-header bg-white py-3 border-bottom d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 8 }}>
              <h5 className="card-title mb-0 font-weight-bold text-dark">
                <i className="fa-solid fa-road mr-2 text-teal" />Active Transit Ledger
              </h5>
              <div className="d-flex align-items-center flex-wrap" style={{ gap: 8 }}>
                <div className="input-group input-group-sm" style={{ maxWidth: 220 }}>
                  <div className="input-group-prepend">
                    <span className="input-group-text bg-white border-right-0">
                      <i className="fa-solid fa-search text-muted" style={{ fontSize: '0.75rem' }} />
                    </span>
                  </div>
                  <input type="text" className="form-control border-left-0" placeholder="Search outlet, driver, vehicle..."
                    value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} data-testid="transfer-search" />
                </div>
                <select className="form-control form-control-sm custom-select" style={{ maxWidth: 160 }}
                  value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="transfer-status-filter">
                  {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s === 'ALL' ? 'All Statuses' : s}</option>)}
                </select>
              </div>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive" style={{ maxHeight: 380, overflowY: 'auto' }}>
                <table className="table table-hover mb-0 text-center" data-testid="transit-table">
                  <thead className="thead-light" style={{ position: 'sticky', top: 0 }}>
                    <tr>
                      <th className="text-left" style={{ paddingLeft: 16 }}>Transfer ID</th>
                      <th className="text-left">Destination</th>
                      <th>Dispatch Time</th>
                      <th>Driver</th>
                      <th>Vehicle</th>
                      <th>Cold Chain</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLedger.length === 0 && (
                      <tr><td colSpan={8} className="text-muted py-4" data-testid="ledger-empty">No transfers found.</td></tr>
                    )}
                    {filteredLedger.map((t) => (
                      <tr key={t.id} data-testid={`transfer-row-${t.id}`}>
                        <td className="text-left font-weight-bold" style={{ paddingLeft: 16, color: '#008080' }}>{t.transfer_ref}</td>
                        <td className="text-left">{t.outlet_name || '—'}</td>
                        <td className="small text-muted">{fmtDt(t.dispatched_at)}</td>
                        <td className="small">{t.driver_name || '--'}</td>
                        <td className="small">{t.vehicle_details || '--'}</td>
                        <td className="small">{t.refrigeration_status || '--'}</td>
                        <td><StatusPill status={t.status} /></td>
                        <td>
                          {isSupervisor ? (
                            <div className="d-flex align-items-center justify-content-center" style={{ gap: 4 }}>
                              <button className="btn btn-sm btn-outline-secondary" onClick={() => openView(t.id)} data-testid={`btn-view-${t.id}`}>
                                <i className="fa-solid fa-eye mr-1" />View
                              </button>
                              {t.status === 'Dispatched' && (
                                <button className="btn btn-sm btn-outline-success" onClick={() => openReceiptModal(t.id)} data-testid={`btn-confirm-receipt-${t.id}`}>
                                  Confirm Receipt
                                </button>
                              )}
                            </div>
                          ) : (
                            t.status === 'Dispatched' ? (
                              <button className="btn btn-sm btn-success px-3" onClick={() => openReceiptModal(t.id)} data-testid={`btn-confirm-${t.id}`}>
                                <i className="fa-solid fa-clipboard-check mr-1" />Confirm
                              </button>
                            ) : (
                              <button className="btn btn-sm btn-outline-secondary" onClick={() => openView(t.id)} data-testid={`btn-view-${t.id}`}>
                                <i className="fa-solid fa-eye mr-1" />View
                              </button>
                            )
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Shortage & Discrepancy Alert Board */}
          <div className="card shadow-sm">
            <div className="card-header bg-white py-3 border-bottom d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0 font-weight-bold text-dark">
                <i className="fa-solid fa-triangle-exclamation mr-2 text-warning" />Shortage &amp; Discrepancy Alert Board
              </h5>
              <span className="small text-muted">Open issues remain visible until reviewed</span>
            </div>
            <div className="card-body" style={{ maxHeight: 220, overflowY: 'auto' }}>
              {discrepancyTransfers.length === 0 ? (
                <div className="text-center text-muted py-3" data-testid="discrepancy-empty">
                  <i className="fa-solid fa-circle-check text-success mr-2" />No open discrepancies.
                </div>
              ) : (
                discrepancyTransfers.map((t) => (
                  <div key={t.id} className="d-flex align-items-start p-3 mb-2 border rounded bg-light" style={{ gap: 12 }} data-testid={`discrepancy-alert-${t.id}`}>
                    <span style={{ color: '#ef4444', fontSize: '1.1rem', marginTop: 2 }}>
                      <i className="fa-solid fa-triangle-exclamation" />
                    </span>
                    <div style={{ flex: 1 }}>
                      <div className="font-weight-bold small" style={{ color: '#008080' }}>{t.transfer_ref}</div>
                      <div className="small text-muted">Outlet: {t.outlet_name || '—'} · Dispatched: {fmtDt(t.dispatched_at)}</div>
                      {t.discrepancy_notes && <div className="small mt-1 text-dark">{t.discrepancy_notes}</div>}
                    </div>
                    <span className="badge" style={{ background: '#fef3c7', color: '#92400e', padding: '3px 8px', borderRadius: 4, fontSize: '0.7rem' }}>Discrepancy</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── New Dispatch Modal (create pending) ── */}
      {newDispatchOpen && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1055 }} data-testid="new-dispatch-modal">
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content shadow-lg border-0" style={{ borderRadius: 14 }}>
              <div className="modal-header" style={{ background: '#008080', color: '#fff', borderRadius: '14px 14px 0 0' }}>
                <h5 className="modal-title font-weight-bold"><i className="fa-solid fa-truck-ramp-box mr-2" />New Transfer Dispatch</h5>
                <button type="button" className="close" style={{ color: '#fff', opacity: 1 }} onClick={() => setNewDispatchOpen(false)}><span>&times;</span></button>
              </div>
              <div className="modal-body p-4">
                {newError && <div className="alert alert-danger py-2" data-testid="new-dispatch-error">{newError}</div>}
                <div className="form-group">
                  <label className="font-weight-600">Destination Outlet</label>
                  <input type="text" className="form-control" placeholder="Outlet name" value={newForm.outlet_name} onChange={(e) => setNewField('outlet_name', e.target.value)} data-testid="new-dispatch-outlet" />
                </div>
                <hr />
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <strong>Items to Dispatch</strong>
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={addNewLine} data-testid="btn-add-line">+ Add Line</button>
                </div>
                {newForm.lines.map((line, idx) => (
                  <div className="form-row align-items-end mb-2" key={idx} data-testid={`new-line-${idx}`}>
                    <div className="form-group col-5 mb-0">
                      <select className="form-control custom-select" value={line.item_id} onChange={(e) => setNewLine(idx, 'item_id', e.target.value)} data-testid={`new-line-item-${idx}`}>
                        <option value="" disabled>Select item...</option>
                        {items.map((i) => <option key={i.id} value={i.id}>{i.item_code} — {i.name}</option>)}
                      </select>
                    </div>
                    <div className="form-group col-3 mb-0">
                      <input type="number" className="form-control" min="0.001" step="0.001" placeholder="Qty"
                        value={line.dispatched_qty} onChange={(e) => setNewLine(idx, 'dispatched_qty', e.target.value)} data-testid={`new-line-qty-${idx}`} />
                    </div>
                    <div className="form-group col-2 mb-0">
                      <input type="text" className="form-control" placeholder="Unit" value={line.unit} readOnly />
                    </div>
                    <div className="form-group col-2 mb-0">
                      {newForm.lines.length > 1 && (
                        <button type="button" className="btn btn-sm btn-outline-danger btn-block" onClick={() => removeNewLine(idx)}>Remove</button>
                      )}
                    </div>
                  </div>
                ))}
                <div className="form-group mt-3">
                  <label className="font-weight-600">Notes</label>
                  <textarea className="form-control" rows="2" placeholder="Any preparation notes..." value={newForm.notes} onChange={(e) => setNewField('notes', e.target.value)} />
                </div>
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary" onClick={() => setNewDispatchOpen(false)}>Cancel</button>
                <button type="button" className="btn btn-teal" onClick={submitNewDispatch} disabled={newSubmitting} data-testid="btn-submit-new-dispatch">
                  <i className="fa-solid fa-plus mr-1" />{newSubmitting ? 'Creating...' : 'Create Pending Transfer'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Execute Dispatch Modal ── */}
      {execDispatchTransfer && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1055 }} data-testid="exec-dispatch-modal">
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content shadow-lg border-0" style={{ borderRadius: 14 }}>
              <div className="modal-header" style={{ background: '#008080', color: '#fff', borderRadius: '14px 14px 0 0' }}>
                <h5 className="modal-title font-weight-bold"><i className="fa-solid fa-truck-ramp-box mr-2" />Dispatch Shipment</h5>
                <button type="button" className="close" style={{ color: '#fff', opacity: 1 }} onClick={() => setExecDispatchTransfer(null)}><span>&times;</span></button>
              </div>
              <div className="modal-body p-4">
                {execError && <div className="alert alert-danger py-2" data-testid="exec-dispatch-error">{execError}</div>}
                <div className="row mb-4">
                  <div className="col-md-6 border-right">
                    <h6 className="font-weight-bold text-teal mb-3"><i className="fa-solid fa-circle-info mr-1" />Transfer Summary</h6>
                    <table className="table table-sm table-borderless mb-0 text-left">
                      <tbody>
                        <tr><th className="text-muted" style={{ width: 110 }}>Transfer ID</th><td className="font-weight-bold">{execDispatchTransfer.transfer_ref}</td></tr>
                        <tr><th className="text-muted">Destination</th><td>{execDispatchTransfer.outlet_name || '—'}</td></tr>
                        <tr><th className="text-muted">Created</th><td>{fmtDt(execDispatchTransfer.created_at)}</td></tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="col-md-6 pl-md-4">
                    <h6 className="font-weight-bold text-teal mb-3"><i className="fa-solid fa-temperature-half mr-1" />Cold Chain Confirmation</h6>
                    <div className="small text-muted">Log vehicle details, refrigerator state, and timestamp before moving the transfer into transit.</div>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group col-md-6">
                    <label className="font-weight-600">Driver Name <span className="text-danger">*</span></label>
                    <input type="text" className="form-control" placeholder="Assigned driver"
                      value={execForm.driver_name} onChange={(e) => setExecForm((f) => ({ ...f, driver_name: e.target.value }))} data-testid="exec-driver" />
                  </div>
                  <div className="form-group col-md-6">
                    <label className="font-weight-600">Vehicle Number <span className="text-danger">*</span></label>
                    <input type="text" className="form-control" placeholder="Plate number"
                      value={execForm.vehicle_details} onChange={(e) => setExecForm((f) => ({ ...f, vehicle_details: e.target.value }))} data-testid="exec-vehicle" />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group col-md-6">
                    <label className="font-weight-600">Refrigerator Status <span className="text-danger">*</span></label>
                    <select className="form-control custom-select"
                      value={execForm.refrigeration_status} onChange={(e) => setExecForm((f) => ({ ...f, refrigeration_status: e.target.value }))} data-testid="exec-refrigeration">
                      {REFRIG_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div className="form-group col-md-6">
                    <label className="font-weight-600">Dispatch Timestamp</label>
                    <input type="datetime-local" className="form-control"
                      value={execForm.dispatched_at} onChange={(e) => setExecForm((f) => ({ ...f, dispatched_at: e.target.value }))} data-testid="exec-dispatch-time" />
                  </div>
                </div>
                <div className="form-group mb-0">
                  <label className="font-weight-600">Dispatch Notes</label>
                  <textarea className="form-control" rows="3" placeholder="Dock notes, temperature observations, special handoff instructions..."
                    value={execForm.dispatch_notes} onChange={(e) => setExecForm((f) => ({ ...f, dispatch_notes: e.target.value }))} />
                </div>
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary" onClick={() => setExecDispatchTransfer(null)}>Cancel</button>
                <button type="button" className="btn btn-teal" onClick={submitExecDispatch} disabled={execSubmitting} data-testid="btn-confirm-dispatch">
                  <i className="fa-solid fa-paper-plane mr-1" />{execSubmitting ? 'Dispatching...' : 'Confirm Dispatch'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── View Transfer Modal ── */}
      {(viewTransfer || viewLoading) && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1055 }} data-testid="view-modal">
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content shadow-lg border-0" style={{ borderRadius: 14 }}>
              <div className="modal-header" style={{ background: '#008080', color: '#fff', borderRadius: '14px 14px 0 0' }}>
                <h5 className="modal-title font-weight-bold"><i className="fa-solid fa-file-lines mr-2" />Transfer Details</h5>
                <button type="button" className="close" style={{ color: '#fff', opacity: 1 }} onClick={() => setViewTransfer(null)}><span>&times;</span></button>
              </div>
              <div className="modal-body p-4">
                {viewLoading && <div className="text-center py-4"><i className="fa-solid fa-spinner fa-spin mr-2" />Loading...</div>}
                {viewTransfer && (
                  <>
                    <div className="row mb-3">
                      <div className="col-md-6 border-right">
                        <h6 className="font-weight-bold text-teal mb-3"><i className="fa-solid fa-circle-info mr-1" />Transfer Header</h6>
                        <table className="table table-sm table-borderless mb-0 text-left">
                          <tbody>
                            <tr><th className="text-muted" style={{ width: 110 }}>Transfer ID</th><td className="font-weight-bold">{viewTransfer.transfer_ref}</td></tr>
                            <tr><th className="text-muted">Destination</th><td>{viewTransfer.outlet_name || '—'}</td></tr>
                            <tr><th className="text-muted">Driver</th><td>{viewTransfer.driver_name || '--'}</td></tr>
                            <tr><th className="text-muted">Vehicle</th><td>{viewTransfer.vehicle_details || '--'}</td></tr>
                            <tr><th className="text-muted">Cold Chain</th><td>{viewTransfer.refrigeration_status || '--'}</td></tr>
                            <tr><th className="text-muted">Status</th><td><StatusPill status={viewTransfer.status} /></td></tr>
                          </tbody>
                        </table>
                      </div>
                      <div className="col-md-6 pl-md-4">
                        <h6 className="font-weight-bold text-teal mb-3"><i className="fa-solid fa-clock mr-1" />Timeline</h6>
                        <table className="table table-sm table-borderless mb-0 text-left">
                          <tbody>
                            <tr><th className="text-muted" style={{ width: 110 }}>Dispatched</th><td>{fmtDt(viewTransfer.dispatched_at)}</td></tr>
                            <tr><th className="text-muted">Received</th><td>{fmtDt(viewTransfer.received_at)}</td></tr>
                            {viewTransfer.has_discrepancy && <tr><th className="text-muted text-danger">Discrepancy</th><td className="text-danger small">{viewTransfer.discrepancy_notes}</td></tr>}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="table-responsive border rounded bg-white">
                      <table className="table table-sm table-hover mb-0 text-center">
                        <thead className="thead-light">
                          <tr>
                            <th className="text-left">Item</th>
                            <th>Dispatched Qty</th>
                            <th>Received Qty</th>
                            <th>Discrepancy</th>
                          </tr>
                        </thead>
                        <tbody>
                          {viewTransfer.items.map((item) => {
                            const disc = item.discrepancy_qty ? Number(item.discrepancy_qty) : 0;
                            return (
                              <tr key={item.id}>
                                <td className="text-left"><strong>{item.item_name}</strong> <small className="text-muted">{item.item_code}</small></td>
                                <td>{Number(item.dispatched_qty).toFixed(2)} {item.unit}</td>
                                <td>{item.received_qty != null ? `${Number(item.received_qty).toFixed(2)} ${item.unit}` : '--'}</td>
                                <td>{disc !== 0 ? <span className="badge badge-warning">{disc > 0 ? `-${disc.toFixed(2)}` : `+${Math.abs(disc).toFixed(2)}`}</span> : <span className="text-muted">—</span>}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary" onClick={() => setViewTransfer(null)}>Close</button>
                {viewTransfer?.status === 'Dispatched' && (
                  <button type="button" className="btn btn-teal" onClick={() => { setViewTransfer(null); openReceiptModal(viewTransfer.id); }} data-testid="btn-view-to-receipt">
                    <i className="fa-solid fa-clipboard-check mr-1" />Confirm Receipt
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Receipt Confirmation Modal ── */}
      {receiptTransfer && (
        <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1060 }} data-testid="receipt-modal">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content shadow-lg border-0" style={{ borderRadius: 14 }}>
              <div className="modal-header" style={{ background: '#008080', color: '#fff', borderRadius: '14px 14px 0 0' }}>
                <h5 className="modal-title font-weight-bold"><i className="fa-solid fa-clipboard-check mr-2" />Delivery Receipt Confirmation</h5>
                <button type="button" className="close" style={{ color: '#fff', opacity: 1 }} onClick={() => setReceiptTransfer(null)}><span>&times;</span></button>
              </div>
              <div className="modal-body p-4">
                {receiptError && <div className="alert alert-danger py-2" data-testid="receipt-modal-error">{receiptError}</div>}
                <div className="row mb-4">
                  <div className="col-md-6 border-right">
                    <h6 className="font-weight-bold text-teal mb-3"><i className="fa-solid fa-circle-info mr-1" />Transfer Header</h6>
                    <table className="table table-sm table-borderless mb-0 text-left">
                      <tbody>
                        <tr><th className="text-muted" style={{ width: 110 }}>Transfer ID</th><td className="font-weight-bold">{receiptTransfer.transfer_ref}</td></tr>
                        <tr><th className="text-muted">Destination</th><td>{receiptTransfer.outlet_name || '—'}</td></tr>
                        <tr><th className="text-muted">Driver</th><td>{receiptTransfer.driver_name || '--'}</td></tr>
                        <tr><th className="text-muted">Vehicle</th><td>{receiptTransfer.vehicle_details || '--'}</td></tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="col-md-6 pl-md-4">
                    <h6 className="font-weight-bold text-teal mb-3"><i className="fa-solid fa-scale-balanced mr-1" />Receipt Guidance</h6>
                    <div className="small text-muted">Outlet managers must confirm actual received quantities for each line. Any variance automatically requires a discrepancy note.</div>
                  </div>
                </div>
                <div className="table-responsive border rounded bg-white mb-3" data-testid="receipt-items-table">
                  <table className="table table-sm table-hover mb-0 text-center">
                    <thead className="thead-light">
                      <tr>
                        <th className="text-left">Item</th>
                        <th>Dispatched Qty</th>
                        <th>Received Qty</th>
                        <th>Variance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {receiptTransfer.items.map((item) => {
                        const received = Number(receivedQties[item.id] ?? item.dispatched_qty);
                        const dispatched = Number(item.dispatched_qty);
                        const diff = dispatched - received;
                        return (
                          <tr key={item.id} data-testid={`receipt-item-row-${item.id}`}>
                            <td className="text-left"><strong>{item.item_name || item.item_code}</strong></td>
                            <td>{dispatched.toFixed(3)} {item.unit}</td>
                            <td>
                              <input type="number" className={`form-control form-control-sm text-center mx-auto ${diff !== 0 ? 'border-warning' : ''}`}
                                style={{ maxWidth: 100 }} min="0" step="0.001"
                                value={receivedQties[item.id] ?? String(dispatched)}
                                onChange={(e) => setReceivedQties((q) => ({ ...q, [item.id]: e.target.value }))}
                                data-testid={`receipt-received-qty-${item.id}`}
                              />
                            </td>
                            <td>
                              {diff !== 0
                                ? <span className="badge badge-warning">{diff > 0 ? `-${diff.toFixed(3)}` : `+${Math.abs(diff).toFixed(3)}`} {item.unit}</span>
                                : <span className="text-muted">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {hasDiscrepancy && (
                  <div className={`form-group mb-0 ${hasDiscrepancy ? '' : 'd-none'}`} data-testid="receipt-discrepancy-group">
                    <label className="font-weight-600 text-danger">
                      <i className="fa-solid fa-triangle-exclamation mr-1" />Discrepancy Note <span className="text-danger">*</span>
                    </label>
                    <textarea className="form-control border-danger" rows="3"
                      placeholder="Explain the shortage, damage, or variance observed at the outlet."
                      value={discrepancyNotes} onChange={(e) => setDiscrepancyNotes(e.target.value)}
                      data-testid="receipt-discrepancy-notes" />
                  </div>
                )}
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary" onClick={() => setReceiptTransfer(null)}>Cancel</button>
                <button type="button" className="btn btn-teal" onClick={submitReceipt} disabled={receiptSubmitting} data-testid="btn-submit-receipt">
                  <i className="fa-solid fa-check-circle mr-1" />{receiptSubmitting ? 'Confirming...' : 'Confirm Receipt'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
